import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const AcademicCapIcon = () => (
  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
  </svg>
);

const values = [
  { name: 'Confident & Well-Rounded', desc: 'We empower students to become confident, well-rounded individuals ready for whatever comes next.' },
  { name: 'Voices That Uplift', desc: 'Students are encouraged to use their voices to uplift both our local community and the world beyond.' },
  { name: 'Creativity & Connection', desc: 'We cultivate an environment filled with creativity, connection, and pride for every child.' },
];

const OurStudentsPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />

    <main id="main-content" className="pt-0">
      {/* Hero */}
      <section
        className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
      >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <AcademicCapIcon />
              <span className="text-sm font-semibold text-white tracking-tight">Academics</span>
            </div>
          </div>
          <h1 className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Our Students
          </h1>
          <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
            A vibrant gem in the heart of East Harlem, celebrating the unique talents and gifts of our diverse students.
          </p>
        </div>
      </section>

      {/* Section — image left, text right */}
      <section className="flex flex-col lg:flex-row min-h-[460px]">
        <div className="lg:w-1/2 h-64 lg:h-auto overflow-hidden">
          <img
            src="/assets/images/ourstudents/20260430_121603__1_.jpg"
            alt="PS 96 students in East Harlem"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="lg:w-1/2 bg-[#00213a] flex items-center px-10 py-14 lg:px-16">
          <div className="max-w-lg">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#fdbb2e] mb-3">East Harlem</p>
            <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-6 leading-tight" style={{ letterSpacing: '-0.02em' }}>
              A Vibrant Gem in the Heart of Harlem.
            </h2>
            <p className="text-white/80 leading-relaxed text-lg">
              PS96 is a vibrant gem in the heart of East Harlem, where generations of families have come together to celebrate the unique talents and gifts of our diverse students. Here, we embrace the rich history and cultural legacy of Harlem, weaving it into every aspect of our community.
            </p>
          </div>
        </div>
      </section>

      {/* Section — text left, image right */}
      <section className="flex flex-col-reverse lg:flex-row min-h-[460px]">
        <div className="lg:w-1/2 bg-white flex items-center px-10 py-14 lg:px-16">
          <div className="max-w-lg">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#00213a] mb-3">Confident Voices</p>
            <h2 className="text-3xl lg:text-4xl font-semibold text-gray-900 mb-6 leading-tight" style={{ letterSpacing: '-0.02em' }}>
              Inspired to Make Their Mark.
            </h2>
            <p className="text-gray-600 leading-relaxed text-lg">
              We empower our students to become confident, well-rounded individuals, encouraging them to use their voices to uplift both our local community and the world beyond. Together, we cultivate an environment filled with creativity, connection, and pride, ensuring that every child feels celebrated and inspired to make their mark.
            </p>
          </div>
        </div>
        <div className="lg:w-1/2 h-64 lg:h-auto overflow-hidden">
          <img
            src="/assets/images/ourstudents/IMG_0995.JPEG"
            alt="PS 96 students celebrating"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Values */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-20">
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdbb2e]/5 rounded-lg border border-[#fdbb2e]/30 mb-4">
            <span className="text-sm font-semibold text-[#00213a]">What We Nurture</span>
          </div>
          <h2 className="text-4xl font-semibold text-gray-900 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Celebrated, Connected, Inspired
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {values.map((item) => (
            <div key={item.name} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md hover:border-[#fdbb2e]/40 transition-all duration-200">
              <div className="w-8 h-8 rounded-lg bg-[#fdbb2e]/10 flex items-center justify-center mb-4">
                <svg className="w-4 h-4 text-[#00213a]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">{item.name}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Photo Grid */}
      <section className="bg-[#00213a] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-[#fdbb2e] mb-6 text-center">Our Community</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              '/assets/images/ourstudents/IMG_2820.jpeg',
              '/assets/images/ourstudents/IMG_3188.jpeg',
            ].map((src) => (
              <div key={src} className="aspect-[4/3] overflow-hidden rounded-xl">
                <img
                  src={src}
                  alt="PS 96 students"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-semibold text-gray-900 mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Discover the PS 96 Difference
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Every child here feels celebrated and inspired to make their mark.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/special-subjects"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#00213a] text-white font-semibold rounded-xl hover:bg-[#003060] transition-colors duration-200"
            >
              Explore Special Subjects <ArrowRightIcon />
            </a>
            <a
              href="/contact-us"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-[#00213a] text-[#00213a] font-semibold rounded-xl hover:bg-[#00213a] hover:text-white transition-colors duration-200"
            >
              Contact Us <ArrowRightIcon />
            </a>
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default OurStudentsPage;
